/**
 * Cookie Storage Provider
 *
 * Stores data in first-party cookies for cross-subdomain tracking
 * and improved persistence compared to localStorage.
 *
 * Benefits over localStorage:
 * - Cross-subdomain: shop.example.com and www.example.com share cookies
 * - Server access: Cookies can be read by server for identity bridging
 * - ITP handling: Can be extended with server-side cookie setting to bypass Safari ITP
 *
 * Limitations:
 * - Safari ITP: JavaScript-set cookies expire after 7 days
 * - Size: 4KB limit per cookie (vs 5MB for localStorage)
 */

import { type StorageProvider } from "../storage.js";

export interface CookieOptions {
  /**
   * Cookie domain (e.g., ".example.com" for cross-subdomain)
   * Leave undefined to use current domain only
   */
  domain?: string;

  /**
   * Expiry in days (default: 730 = 2 years)
   * Note: Safari ITP limits JS-set cookies to 7 days
   */
  expiryDays?: number;

  /**
   * SameSite attribute
   * - "Strict": Only sent in first-party context
   * - "Lax": Sent on top-level navigations (default, good for analytics)
   * - "None": Sent in all contexts (requires Secure)
   */
  sameSite?: "Strict" | "Lax" | "None";

  /**
   * Secure attribute - only send over HTTPS
   * Default: true in production, false in development
   */
  secure?: boolean;

  /**
   * Path for the cookie (default: "/")
   */
  path?: string;
}

const DEFAULT_OPTIONS: Required<CookieOptions> = {
  domain: "",
  expiryDays: 730, // 2 years
  sameSite: "Lax",
  secure: typeof window !== "undefined" && window.location.protocol === "https:",
  path: "/",
};

/**
 * Parse all cookies into a key-value object
 */
function parseCookies(): Record<string, string> {
  if (typeof document === "undefined") {
    return {};
  }

  const cookies: Record<string, string> = {};
  const pairs = document.cookie.split(";");

  for (const pair of pairs) {
    const [key, ...valueParts] = pair.split("=");
    if (key) {
      const trimmedKey = key.trim();
      const value = valueParts.join("=").trim();
      if (trimmedKey) {
        cookies[trimmedKey] = decodeURIComponent(value);
      }
    }
  }

  return cookies;
}

/**
 * Set a cookie with the given options
 */
function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {},
): void {
  if (typeof document === "undefined") {
    return;
  }

  const opts = { ...DEFAULT_OPTIONS, ...options };

  const expires = new Date();
  expires.setDate(expires.getDate() + opts.expiryDays);

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  cookieString += `; expires=${expires.toUTCString()}`;
  cookieString += `; path=${opts.path}`;

  if (opts.domain) {
    cookieString += `; domain=${opts.domain}`;
  }

  cookieString += `; SameSite=${opts.sameSite}`;

  if (opts.secure) {
    cookieString += "; Secure";
  }

  document.cookie = cookieString;
}

/**
 * Delete a cookie by setting it to expire immediately
 */
function deleteCookie(name: string, options: CookieOptions = {}): void {
  if (typeof document === "undefined") {
    return;
  }

  const opts = { ...DEFAULT_OPTIONS, ...options };

  let cookieString = `${encodeURIComponent(name)}=`;
  cookieString += "; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  cookieString += `; path=${opts.path}`;

  if (opts.domain) {
    cookieString += `; domain=${opts.domain}`;
  }

  document.cookie = cookieString;
}

/**
 * Cookie-based storage provider
 *
 * Implements StorageProvider interface for cookie-based persistence.
 */
class CookieStorageProvider implements StorageProvider {
  private options: CookieOptions;
  private prefix: string;

  constructor(options: CookieOptions = {}, prefix: string = "") {
    this.options = options;
    this.prefix = prefix;
  }

  private getKey(key: string): string {
    return this.prefix ? `${this.prefix}_${key}` : key;
  }

  getItem(key: string): string | null {
    const cookies = parseCookies();
    return cookies[this.getKey(key)] ?? null;
  }

  setItem(key: string, value: string): void {
    setCookie(this.getKey(key), value, this.options);
  }

  removeItem(key: string): void {
    deleteCookie(this.getKey(key), this.options);
  }
}

/**
 * Get a cookie storage provider with the given options
 *
 * @example
 * // Basic usage (current domain only)
 * const storage = getCookieStorage();
 *
 * @example
 * // Cross-subdomain (shop.example.com + www.example.com)
 * const storage = getCookieStorage({ domain: ".example.com" });
 */
export function getCookieStorage(
  options: CookieOptions = {},
  prefix: string = "",
): StorageProvider {
  return new CookieStorageProvider(options, prefix);
}

/**
 * Utility to get the root domain for cross-subdomain cookies
 *
 * @example
 * getRootDomain("shop.example.com") // returns ".example.com"
 * getRootDomain("www.shop.example.co.uk") // returns ".example.co.uk" (simplified)
 */
export function getRootDomain(hostname: string = window.location.hostname): string {
  // Simple heuristic: take last two parts of domain
  // This doesn't handle all TLDs correctly but works for most cases
  const parts = hostname.split(".");

  // localhost or IP address
  if (parts.length <= 1 || /^\d+$/.test(parts[parts.length - 1])) {
    return hostname;
  }

  // Return last two parts with leading dot
  return "." + parts.slice(-2).join(".");
}


